import React, { memo, useCallback, useMemo } from 'react';
import moment from 'moment-timezone';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useGetOnDemandStaffProfessionalShiftHistoryQuery } from '../../../../redux/onDemandStaffProfessionalShiftHistory';
import LoadingSpinner from '../../../../components/spinner/LoadingSpinner';
import ShiftHistoryPacketActions from './ShiftHistoryPacketActions';

import './OnDemandStaffShiftHistory.scss';

const API_DATE_FORMAT = 'YYYY-MM-DD';
const SHIFT_TIME_FORMAT = 'MMM DD, YYYY [·] h:mm A';

// Scheduled times, shown in the facility's timezone. The raw timestamps are kept for sorting.
const mapShifts = (shifts) =>
	shifts?.map((shift) => ({
		slotId: shift.slotId,
		facilityName: shift.facilityName,
		shiftType: shift.shiftType,
		startTimestamp: moment(shift.startTime).valueOf(),
		endTimestamp: moment(shift.endTime).valueOf(),
		shiftStart: moment.tz(shift.startTime, shift.facilityTimezone).format(SHIFT_TIME_FORMAT),
		shiftEnd: moment.tz(shift.endTime, shift.facilityTimezone).format(SHIFT_TIME_FORMAT),
		hasCredentialPacket: shift.hasCredentialPacket
	})) || [];

const shiftStartTemplate = (row) => row.shiftStart;
const shiftEndTemplate = (row) => row.shiftEnd;

// Shift History for a ShiftMed professional: one row per worked shift at the facilities where the user holds the
// Past Shifts permission, filtered by the applied start-date range.
const OnDemandStaffShiftHistory = ({ managementCompanyId, caregiverId, dateRange }) => {
	const queryArgs = useMemo(
		() => ({
			caregiverId,
			managementCompanyId,
			from: dateRange.from ? moment(dateRange.from).format(API_DATE_FORMAT) : undefined,
			to: dateRange.to ? moment(dateRange.to).format(API_DATE_FORMAT) : undefined
		}),
		[caregiverId, managementCompanyId, dateRange]
	);

	const { data, isFetching } = useGetOnDemandStaffProfessionalShiftHistoryQuery(queryArgs, {
		skip: !managementCompanyId,
		refetchOnMountOrArgChange: true
	});

	const shifts = useMemo(() => mapShifts(data?.shifts), [data]);

	const actionsTemplate = useCallback(
		(row) => (
			<ShiftHistoryPacketActions
				managementCompanyId={managementCompanyId}
				caregiverId={caregiverId}
				slotId={row.slotId}
				hasCredentialPacket={row.hasCredentialPacket}
			/>
		),
		[managementCompanyId, caregiverId]
	);

	if (data?.isEligible && !data.hasPastShiftsPermission) {
		return <p className="shift-history-no-permission">You don’t have permission to view shifts at this time.</p>;
	}

	return (
		<div className="flex-professionals-credentials on-demand-staff-shift-history">
			<DataTable
				value={shifts}
				dataKey="slotId"
				tableStyle={{ tableLayout: 'fixed' }}
				size="small"
				stripedRows
				loading={isFetching}
				loadingIcon={<LoadingSpinner />}
				emptyMessage="No shifts found"
				removableSort
				sortField="startTimestamp"
				sortOrder={-1}
			>
				<Column field="facilityName" header="Facility" style={{ width: '28%' }} sortable />
				<Column field="shiftType" header="Shift Type" style={{ width: '20%' }} sortable />
				<Column
					field="startTimestamp"
					header="Shift Start"
					style={{ width: '22%' }}
					body={shiftStartTemplate}
					sortable
				/>
				<Column field="endTimestamp" header="Shift End" style={{ width: '20%' }} body={shiftEndTemplate} sortable />
				<Column header="Actions" style={{ width: '10%' }} body={actionsTemplate} />
			</DataTable>
		</div>
	);
};

export default memo(OnDemandStaffShiftHistory);
